import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Shared hook for list selection with error-aware auto-selection.
 * Tracks which items have errored and automatically selects the first
 * non-errored item when the current selection fails.
 *
 * Used by both web and web-old for contract list error handling.
 */
export const useContractSelection = <T>(items: T[]) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [erroredIndices, setErroredIndices] = useState<Set<number>>(new Set());

    const handleDetailError = useCallback((index: number) => {
        setErroredIndices((prev) => {
            if (prev.has(index)) return prev;
            const next = new Set(prev);
            next.add(index);
            return next;
        });
    }, []);

    // Stable per-index callbacks to avoid unnecessary effect re-fires in children
    const errorCallbacks = useMemo(
        () => items.map((_, i) => () => handleDetailError(i)),
        [items, handleDetailError],
    );

    // Auto-select first non-errored item when current selection has error
    useEffect(() => {
        if (erroredIndices.has(selectedIndex) && items.length > 0) {
            const firstValid = items.findIndex((_, i) => !erroredIndices.has(i));
            if (firstValid !== -1) {
                setSelectedIndex(firstValid);
            }
        }
    }, [erroredIndices, selectedIndex, items]);

    const allErrored = items.length > 0 && erroredIndices.size >= items.length;

    return {
        selectedIndex,
        setSelectedIndex,
        errorCallbacks,
        erroredIndices,
        allErrored,
    };
};
